import React, { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Package, FileText, Receipt, FileCheck,
  Wallet, ClipboardList, BarChart3, Settings, LogOut, Search, Bell,
  Menu, X, FileSignature, CreditCard
} from 'lucide-react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/customers', label: 'Customers', icon: Users },
  { to: '/products', label: 'Products / Services', icon: Package },
  { to: '/quotations', label: 'Quotations', icon: FileText },
  { to: '/invoices', label: 'Invoices', icon: FileSignature },
  { to: '/receipts', label: 'Receipts', icon: Receipt },
  { to: '/vouchers', label: 'Payment Vouchers', icon: Wallet },
  { to: '/proposals', label: 'Proposals', icon: ClipboardList },
  { to: '/reports', label: 'Reports', icon: BarChart3 },
  { to: '/billing', label: 'Plans & Billing', icon: CreditCard },
  { to: '/settings', label: 'Company Settings', icon: Settings },
];

const SidebarContent = ({ onNavigate, onLogout }) => (
  <div className="flex h-full flex-col">
    <div className="flex items-center gap-3 px-5 py-5">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 shadow-lg shadow-blue-900/40">
        <FileCheck className="h-5 w-5 text-white" />
      </div>
      <span className="text-[15px] font-bold text-white tracking-tight">BizDocs Manager</span>
    </div>
    <nav className="sidebar-scroll flex-1 space-y-1 overflow-y-auto px-3 py-2">
      {NAV.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
              isActive
                ? 'bg-blue-600 text-white shadow-md shadow-blue-900/30'
                : 'text-slate-300 hover:bg-white/5 hover:text-white'
            }`
          }
        >
          <Icon className="h-[18px] w-[18px] shrink-0" />
          <span className="truncate">{label}</span>
        </NavLink>
      ))}
    </nav>
    <div className="px-3 pb-4">
      <button onClick={onLogout} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-white">
        <LogOut className="h-[18px] w-[18px]" />
        Log out
      </button>
    </div>
  </div>
);

const PAGE_TITLES = {
  '/': 'Dashboard', '/customers': 'Customers', '/products': 'Products / Services',
  '/quotations': 'Quotations', '/invoices': 'Invoices', '/receipts': 'Receipts',
  '/vouchers': 'Payment Vouchers', '/proposals': 'Proposals', '/reports': 'Reports',
  '/billing': 'Plans & Billing', '/settings': 'Company Settings',
};

export default function Layout() {
  const { company } = useData();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const title = PAGE_TITLES[location.pathname] || 'BizDocs';
  const initials = (user?.name || 'Admin').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 bg-slate-900 lg:block">
        <SidebarContent onLogout={logout} />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-slate-900 animate-fade-in">
            <button onClick={() => setMobileOpen(false)} className="absolute right-3 top-4 text-slate-400">
              <X className="h-5 w-5" />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} onLogout={logout} />
          </aside>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Topbar */}
        <header className="flex h-16 shrink-0 items-center gap-4 border-b border-slate-200 bg-white px-4 lg:px-6">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)}>
            <Menu className="h-6 w-6 text-slate-700" />
          </button>
          <div className="relative hidden max-w-md flex-1 sm:block">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search documents, customers..." className="h-10 rounded-full border-slate-200 bg-slate-50 pl-10 focus-visible:ring-blue-500" />
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button className="relative rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100">
              <Bell className="h-5 w-5" />
              <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-500" />
            </button>
            <div className="flex items-center gap-2.5">
              <Avatar className="h-9 w-9">
                {user?.picture && <AvatarImage src={user.picture} alt={user?.name} />}
                <AvatarFallback className="bg-blue-600 text-xs font-semibold text-white">{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden text-right sm:block">
                <p className="text-sm font-semibold leading-tight text-slate-800">{user?.name || 'Admin'}</p>
                <p className="text-xs leading-tight text-slate-500">{company.name}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl p-4 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
